import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Space, Table, Tag, Typography } from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AssignHomeworkDrawer } from './AssignHomeworkDrawer';
import {
  SINCE_LAST_SESSION_FIELDS,
  occurrenceLabel,
  sentenceDayOf,
  statusChipOf,
  type SinceLastSessionRow,
} from './model';
import { useHomeworkLabels } from './useHomeworkLabels';

/**
 * E34 · HomeworkSinceLastSession — "Session form — Homework since last session" (cc-homework, chapter
 * "Homework — Assign & follow (portal, E34)"): the session form's Review Homework panel (E17) — what
 * the client did since the PREVIOUS session, in completion order, with the record each completion
 * points at; the ones still open below (overdue first). Reads `homeworkSinceLastSession(subject,
 * sessionId)`; the boundary is the session before the one being written (none: a first session).
 *
 * Behavior lines implemented here:
 *   · the evidence link opens the client's record in the hub — the hub's Homework tab, where the row
 *     and its evidence are (the tools' own pages have no by-record address yet — finding F-16);
 *   · the heading reads "Since <date>" — the boundary session's start (`lastSessionStart`, the
 *     founder's Q-b1b-4 A); a first session reads everything so far;
 *   · a recurring row lists its occurrences answered since then ("5 Oct: done") and stays open while
 *     an occurrence is open (Q-b1c-6 C); each occurrence links its OWN record (Q-b1e-3 A); a done one
 *     answered again inside its window reads "· repeated 1" (the hub's key, Q-b1e-6 A);
 *   · i18n homework.session.*;
 *   · a video step's evidence opens nothing (video is block 6 — the drawer does not offer it yet).
 * "Plan for next session": Assign homework opens the same drawer as the client hub.
 */
type Props = {
  clientId: string | undefined;
  clientName: string;
  /** The session being written — its edge id; absent while the form has not saved it. */
  sessionId: string | undefined;
  locale: 'en' | 'ru';
};

export const HomeworkSinceLastSessionPanel = ({
  clientId,
  clientName,
  sessionId,
  locale,
}: Props) => {
  const t = useTranslate();
  const labels = useHomeworkLabels();
  const [assigning, setAssigning] = useState(false);

  const { data, isFetching, isError, refetch } = useCustom<
    SinceLastSessionRow[]
  >({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'homeworkSinceLastSession',
      fields: SINCE_LAST_SESSION_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        sessionId: { name: 'sessionId', type: 'ID', value: sessionId || null },
      },
    },
    queryOptions: { enabled: Boolean(clientId) },
  });
  // the client's language (Q-fix-1 C): the Assign drawer's video list offers only that catalogue
  const { data: clientData } = useCustom<{ _id: string; lng?: string | null }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'client',
      fields: ['_id', 'lng'],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const rows = useMemo(() => data?.data ?? [], [data]);
  const closed = rows.filter((r) => r.status !== 'open');
  const open = rows.filter((r) => r.status === 'open');
  const boundary = rows[0]?.sessionId ?? null;
  const since = rows[0]?.lastSessionStart ?? null;

  const dateLocale = locale === 'ru' ? ru : enUS;
  const when = (iso: string | null | undefined) =>
    iso
      ? format(parseISO(iso), 'd MMM yyyy, HH:mm', { locale: dateLocale })
      : '—';

  if (!clientId) {
    return (
      <Typography.Paragraph
        type="secondary"
        data-testid="homework-session-pick-client"
      >
        {t('homework.session.pickClient')}
      </Typography.Paragraph>
    );
  }

  const hubLink = (
    <Link
      to={`/clients/show/${encodeURIComponent(clientId)}?tab=homework`}
      data-testid="homework-session-open-hub"
    >
      {t('homework.session.openRecord')}
    </Link>
  );

  /** Each occurrence answered since the boundary, with the record it left (or the skip). */
  const outcomesOf = (r: SinceLastSessionRow) => (
    <Space direction="vertical" size={2}>
      {(r.outcomes ?? []).map((o) => (
        <div
          key={`${o.occurrenceAt}-${o.outcome}`}
          data-testid={`homework-session-outcome-${r.assignmentId}-${o.outcome}`}
        >
          <span>
            {t('homework.outcome.line', {
              when: o.occurrenceAt
                ? // D-1e-catchup-2-2026-09-29 (F-68): the occurrence is named in the RULE's zone, as
                  // the hub names it — a one-off row (tzid null) reads the viewer's local day
                  occurrenceLabel(o.occurrenceAt, null, r.tzid ?? null, locale)
                : '—',
              what: t(`homework.outcome.${o.outcome}`),
            })}
          </span>
          {o.outcome === 'done' && o.repeats ? (
            // Q-b1e-6 A: answered again inside its window — never a second done
            <span
              data-testid={`homework-session-outcome-repeats-${r.assignmentId}`}
              style={{ whiteSpace: 'nowrap' }}
            >
              {' · '}
              {t('homework.hub.outcomeRepeats', { count: o.repeats })}
            </span>
          ) : null}
          {o.outcome === 'done' ? (
            <Space direction="vertical" size={0}>
              {labels.evidence(o.evidence)}
              {o.evidence ? hubLink : null}
            </Space>
          ) : null}
        </div>
      ))}
    </Space>
  );

  const baseColumns = [
    {
      title: t('homework.session.column.title'),
      render: (_: unknown, r: SinceLastSessionRow) => (
        <Typography.Text strong>{r.title}</Typography.Text>
      ),
    },
    {
      title: t('homework.session.column.kind'),
      width: 110,
      render: (_: unknown, r: SinceLastSessionRow) => labels.kindLabel(r.kind),
    },
    {
      title: t('homework.session.column.status'),
      width: 130,
      render: (_: unknown, r: SinceLastSessionRow) => {
        const chip = statusChipOf(r);
        return (
          <Tag
            color={chip.color}
            data-testid={`homework-session-status-${r.assignmentId}`}
          >
            {t(`homework.status.${chip.key}`)}
          </Tag>
        );
      },
    },
  ];

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%' }}
      data-testid="homework-session-panel"
    >
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('homework.session.title')}
          </Typography.Title>
          <Typography.Text
            type="secondary"
            data-testid="homework-session-boundary"
          >
            {rows.length === 0
              ? ''
              : boundary
              ? t('homework.session.since', {
                  date: since ? sentenceDayOf(since, dateLocale) : '—',
                })
              : t('homework.session.first')}
          </Typography.Text>
        </div>
        <Button
          onClick={() => setAssigning(true)}
          data-testid="homework-session-assign"
        >
          + {t('homework.session.assign')}
        </Button>
      </Space>

      {isError ? (
        <Alert
          type="error"
          showIcon
          message={t('homework.session.loadFailed')}
        />
      ) : null}

      {!isFetching && rows.length === 0 && !isError ? (
        <Typography.Paragraph
          type="secondary"
          data-testid="homework-session-empty"
        >
          {t('homework.session.empty')}
        </Typography.Paragraph>
      ) : null}

      {rows.length > 0 ? (
        <>
          <Typography.Text strong>
            {t('homework.session.doneHeader')}
          </Typography.Text>
          <Table<SinceLastSessionRow>
            rowKey="assignmentId"
            size="small"
            pagination={false}
            loading={isFetching}
            dataSource={closed}
            tableLayout="fixed"
            scroll={{ x: 760 }}
            locale={{ emptyText: t('homework.session.noneSince') }}
            data-testid="homework-session-done"
            onRow={(r) =>
              ({
                'data-testid': `homework-session-row-${r.assignmentId}`,
              } as never)
            }
            columns={[
              ...baseColumns,
              {
                title: t('homework.session.column.when'),
                width: 160,
                render: (_, r) => when(r.outcomeAt),
              },
              {
                // what the client did: the record a completion points at, or the skip reason
                title: t('homework.session.column.outcome'),
                width: 240,
                render: (_, r) =>
                  (r.outcomes ?? []).some((o) => o.occurrenceAt !== null) ? (
                    outcomesOf(r)
                  ) : r.status === 'done' ? (
                    <Space direction="vertical" size={0}>
                      {labels.evidence(r.evidence)}
                      {r.evidence ? hubLink : null}
                    </Space>
                  ) : r.reasonCode ? (
                    <span
                      data-testid={`homework-session-skip-${r.assignmentId}`}
                    >
                      {t(`homework.skipReason.${r.reasonCode}`)}
                      {r.reason ? ` — “${r.reason}”` : ''}
                    </span>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
          <Typography.Text strong>
            {t('homework.session.openHeader')}
          </Typography.Text>
          <Table<SinceLastSessionRow>
            rowKey="assignmentId"
            size="small"
            pagination={false}
            loading={isFetching}
            dataSource={open}
            tableLayout="fixed"
            scroll={{ x: 760 }}
            locale={{ emptyText: t('homework.session.noneOpen') }}
            data-testid="homework-session-open"
            onRow={(r) =>
              ({
                'data-testid': `homework-session-row-${r.assignmentId}`,
              } as never)
            }
            columns={[
              ...baseColumns,
              {
                title: t('homework.session.column.due'),
                width: 160,
                render: (_, r) => when(r.dueAt),
              },
              {
                // a recurring row answered since the boundary: each occurrence with its own record
                title: t('homework.session.column.answered'),
                width: 240,
                render: (_, r) =>
                  (r.outcomes ?? []).length > 0 ? outcomesOf(r) : '—',
              },
            ]}
          />
        </>
      ) : null}

      <AssignHomeworkDrawer
        open={assigning}
        clients={[{ subject: clientId, name: clientName, lng: clientData?.data?.lng }]}
        onClose={() => setAssigning(false)}
        onAssigned={() => refetch()}
      />
    </Space>
  );
};
